import { useState, useRef, useEffect, useCallback } from 'react';

interface UseAutoSaveOptions {
    debounceDelay?: number; // 防抖延迟时间，默认 5000ms
    saveFn: (val?: any) => Promise<any>;
}

export type SaveStatus = 'saving' | 'saved';

export const useAutoSave = (options: UseAutoSaveOptions) => {
    const {
        saveFn,
        debounceDelay = 5000,
    } = options;

    const [saveStatus, setSaveStatus] = useState<SaveStatus>();
    const [hasEverSaved, setHasEverSaved] = useState(false);
    const saveTimerRef = useRef<NodeJS.Timeout | null>(null);
    const lastSavedValueRef = useRef<string>(''); // 上次保存的值
    const pendingValueRef = useRef<string>(''); // 等待保存的值

    // 清理定时器
    useEffect(() => {
        return () => {
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
            }
        };
    }, []);

    const triggerAutoSave = useCallback((currentValue: string) => {
        // 记录当前等待保存的值
        pendingValueRef.current = currentValue;

        setSaveStatus('saving');

        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
        }

        saveTimerRef.current = setTimeout(async () => {
            try {
                // 检查最终值是否与上次保存的值相同
                if (pendingValueRef.current === lastSavedValueRef.current) {
                    setSaveStatus(undefined);
                    return;
                }

                const res = await saveFn(pendingValueRef.current);
                if (!res?.error) {
                    setSaveStatus('saved');
                    setHasEverSaved(true);
                    // 更新上次保存的值
                    lastSavedValueRef.current = pendingValueRef.current;
                }
            } catch (error) {
                console.error('Auto save failed:', error);
                setSaveStatus(undefined);
            }
        }, debounceDelay);
    }, [debounceDelay, saveFn]);

    // 设置初始值（用于重置基准值）
    const setInitialValue = useCallback((value: string) => {
        lastSavedValueRef.current = value;
    }, []);

    return {
        saveStatus,
        hasEverSaved,
        triggerAutoSave,
        setInitialValue,
    };
};

